/**
 * GLSL for the Earth-like planet family (`planetProfiles.ts`): Aubade, and the generic world any other
 * pinned place gets. The same bake/frame split as `planetShaders.ts`, with three differences:
 *
 *  - The surface bake has two outputs: `uOut == 0` is albedo + ocean mask, `uOut == 1` the night
 *    lights (red: coastal cities, green: the lines of light strung between them).
 *  - `uBleach` (0..1) is THE FADING: broad tracts of land drained to pale ash, a sickly straw band at
 *    each front. Aubade runs it at 1; the generic world at 0, a plain temperate world.
 *  - The frame shader adds the night lights and keeps only a thin Fresnel limb; on Aubade the ragged
 *    limb proper is the wind layer's (`windShaders.ts`).
 *
 * Every animated term reads `uTime`, the stage's shared clock.
 */
import { NOISE } from "./planetShaders";

/** Surface albedo + ocean mask (`uOut` 0), or the night-lights map (`uOut` 1). */
export const FADING_SURFACE_BAKE_FRAGMENT = /* glsl */ `
uniform vec3 uSeed;
uniform int uOut;
uniform float uBleach;
varying vec2 vUv;
${NOISE}
void main() {
  vec3 s = spherePoint(vUv);
  vec3 p = s + uSeed;
  vec3 warp = vec3(fbm(p * 1.0, 4), fbm(p * 1.0 + 7.3, 4), fbm(p * 1.0 - 4.1, 4));
  float c = fbm(p * 1.25 + warp * 0.85, 8);
  float e = c - 0.07;
  float land = smoothstep(-0.004, 0.012, e);
  float lat = abs(s.y);
  float detail = fbm(p * 22.0, 4);

  if (uOut == 1) {
    // Night lights: cities cluster on coasts and lowlands; covered walkways string between them.
    float coast = smoothstep(0.16, 0.0, e) * land;
    float region = smoothstep(0.05, 0.45, fbm(p * 3.2 + warp, 4));
    float cluster = smoothstep(0.15, 0.55, fbm(p * 11.0, 4)) * (coast * 0.85 + land * 0.2) * region;
    float speck = smoothstep(0.18, 0.5, fbm(p * 70.0, 3));
    float city = cluster * (0.25 + speck);
    float lines = smoothstep(0.012, 0.0, abs(snoise(p * 9.0 + warp * 0.4))) * smoothstep(0.0, 0.3, region) * land;
    lines *= smoothstep(0.1, 0.4, fbm(p * 5.0 - warp, 3)) * (1.0 - smoothstep(0.75, 0.85, lat));
    gl_FragColor = vec4(clamp(city * 1.4, 0.0, 1.0), clamp(lines, 0.0, 1.0), 0.0, 1.0);
    return;
  }

  // Land: temperate forest and grassland, a little dry scrub, rock on the peaks.
  float forest = smoothstep(-0.15, 0.2, fbm(p * 4.0 + warp * 1.3, 5));
  vec3 grass = vec3(0.20, 0.33, 0.12);
  vec3 wood = vec3(0.07, 0.20, 0.07);
  vec3 scrub = vec3(0.42, 0.38, 0.22);
  vec3 rock = vec3(0.36, 0.34, 0.31);
  vec3 ground = mix(grass, wood, forest);
  ground = mix(ground, scrub, smoothstep(0.05, 0.35, fbm(p * 2.2 - warp, 4)) * smoothstep(0.7, 0.2, lat) * 0.7);
  ground = mix(ground, rock, smoothstep(0.42, 0.58, e + detail * 0.06));
  ground *= 0.85 + 0.3 * detail;

  // THE FADING: broad bleached tracts with mottled edges, a sickly straw band at each front.
  float bleachField = fbm(p * 1.8 + warp * 0.7, 6) + fbm(p * 12.0, 4) * 0.22;
  float bleach = smoothstep(-0.12, 0.12, bleachField);
  float front = smoothstep(0.1, 0.0, abs(bleachField + 0.02)) * (1.0 - bleach * 0.6);
  vec3 bleached = vec3(0.64, 0.62, 0.55) * (0.9 + 0.2 * detail);
  vec3 dying = vec3(0.50, 0.47, 0.27);
  ground = mix(ground, dying, front * 0.6 * uBleach);
  ground = mix(ground, bleached, bleach * 0.9 * uBleach);

  // Seas: clear blue, bright shallows along the coasts.
  vec3 deep = vec3(0.015, 0.06, 0.20);
  vec3 mid = vec3(0.03, 0.14, 0.34);
  vec3 shelf = vec3(0.08, 0.36, 0.48);
  vec3 water = mix(deep, mid, smoothstep(-0.25, -0.05, e));
  water = mix(water, shelf, smoothstep(-0.05, 0.0, e) * 0.85);

  vec3 col = mix(water, ground, land);
  float cap = smoothstep(0.88, 0.95, lat + fbm(p * 6.0, 3) * 0.05);
  col = mix(col, vec3(0.86, 0.9, 0.93), cap * 0.9);
  gl_FragColor = vec4(col, (1.0 - land) * (1.0 - cap));
}
`;

/** Thin, fraying cloud: long torn streaks, little cover. */
export const FADING_CLOUD_BAKE_FRAGMENT = /* glsl */ `
uniform vec3 uSeed;
varying vec2 vUv;
${NOISE}
void main() {
  vec3 s = spherePoint(vUv);
  vec3 p = s + uSeed * 1.7;
  float belts = smoothstep(0.45, 0.95, 0.5 + 0.5 * sin(s.y * 7.0 + 1.3 + fbm(p * 1.2, 3) * 1.8));
  vec3 stretched = vec3(p.x * 1.2, p.y * 14.0, p.z * 1.2);
  float streak = fbm(stretched + fbm(p * 3.0, 4) * 1.1, 7);
  float d = smoothstep(0.02, 0.42, streak) * belts;
  // Torn: a fine noise eats holes through what is left.
  d *= smoothstep(-0.15, 0.25, fbm(p * 9.0, 4));
  gl_FragColor = vec4(clamp(d * 0.75, 0.0, 1.0), 0.0, 0.0, 1.0);
}
`;

/** The planet, lit every frame, with its night lights. */
export const FADING_PLANET_FRAGMENT = /* glsl */ `
uniform sampler2D uSurface;
uniform sampler2D uClouds;
uniform sampler2D uLights;
uniform vec3 uLight;
uniform float uCloudShift;
uniform float uTime;
uniform vec3 uAtmo;
varying vec2 vUv;
varying vec3 vNormalV;
varying vec3 vPosV;
void main() {
  vec3 n = normalize(vNormalV);
  vec3 v = normalize(-vPosV);
  vec3 l = normalize(uLight);
  vec4 surf = texture2D(uSurface, vUv);
  vec2 cuv = vec2(vUv.x + uCloudShift, vUv.y);
  float cloud = texture2D(uClouds, cuv).r;
  float shade = texture2D(uClouds, cuv + vec2(0.0035, -0.0025)).r;

  float ndl = dot(n, l);
  float lambert = max(ndl, 0.0);
  float day = smoothstep(-0.18, 0.32, ndl);

  vec3 albedo = surf.rgb * (1.0 - 0.45 * shade * day);
  vec3 col = albedo * (lambert * 1.3 + 0.03);
  // A thin sky: cool, faint scattering at the terminator rather than a warm band.
  col += albedo * vec3(0.30, 0.34, 0.46) * smoothstep(0.22, 0.0, abs(ndl - 0.02)) * 0.3;

  vec3 h = normalize(l + v);
  float spec = pow(max(dot(n, h), 0.0), 90.0) * surf.a * day;
  col += vec3(0.85, 0.92, 1.0) * spec * 0.7;

  vec3 cloudCol = vec3(0.95, 0.97, 1.0) * (lambert * 1.2 + 0.01);
  col = mix(col, cloudCol, cloud * 0.8);

  // Night lights: sodium-warm cities, cooler strung walkways; a slow flicker on the clock.
  vec4 lt = texture2D(uLights, vUv);
  float night = 1.0 - smoothstep(-0.22, 0.04, ndl);
  float flicker = 0.92 + 0.08 * sin(uTime * 0.7 + vUv.x * 400.0);
  vec3 lights = vec3(1.0, 0.7, 0.36) * lt.r * 2.2 + vec3(0.62, 0.8, 1.0) * lt.g * 1.3;
  col += lights * night * (1.0 - cloud * 0.7) * flicker;

  // Thin limb: the planet keeps a sliver of sky, mostly on the lit side.
  float fres = pow(1.0 - max(dot(n, v), 0.0), 4.0);
  col += uAtmo * fres * (0.04 + 0.7 * smoothstep(-0.1, 0.7, ndl));

  gl_FragColor = vec4(col, 1.0);
}
`;
